import type { TagCategory } from '../types'

export const TAG_CATEGORIES: TagCategory[] = [
  {
    id: 'gender',
    label: 'Gender',
    type: 'character',
    values: [
      { id: 'male', label: 'Male' },
      { id: 'female', label: 'Female' },
      { id: 'random-gender', label: 'Random' },
    ],
  },
  {
    id: 'age',
    label: 'Age Range',
    type: 'character',
    values: [
      { id: '20s', label: '20s' },
      { id: '30s', label: '30s' },
      { id: '40s', label: '40s' },
      { id: '50s', label: '50s' },
      { id: '60s+', label: '60s+' },
    ],
  },
  {
    id: 'attire',
    label: 'Attire',
    type: 'character',
    values: [
      { id: 'casual', label: 'Casual' },
      { id: 'chef-whites', label: 'Chef Whites' },
      { id: 'formal', label: 'Formal' },
      { id: 'athletic', label: 'Athletic' },
    ],
  },
  {
    id: 'accessories',
    label: 'Accessories',
    type: 'character',
    multiSelect: true,
    values: [
      { id: 'apron', label: 'Apron' },
      { id: 'hat', label: 'Hat' },
      { id: 'glasses', label: 'Glasses' },
      { id: 'bandana', label: 'Bandana' },
    ],
  },
  {
    id: 'build',
    label: 'Build',
    type: 'character',
    values: [
      { id: 'slim', label: 'Slim' },
      { id: 'average', label: 'Average' },
      { id: 'athletic-build', label: 'Athletic' },
      { id: 'heavyset', label: 'Heavyset' },
    ],
  },
  {
    id: 'hair',
    label: 'Hair',
    type: 'character',
    values: [
      { id: 'short-dark', label: 'Short Dark' },
      { id: 'short-light', label: 'Short Light' },
      { id: 'long-dark', label: 'Long Dark' },
      { id: 'long-light', label: 'Long Light' },
      { id: 'curly', label: 'Curly' },
      { id: 'bald', label: 'Bald' },
      { id: 'gray', label: 'Gray' },
    ],
  },
  {
    id: 'ethnicity',
    label: 'Ethnicity',
    type: 'character',
    values: [
      { id: 'east-asian', label: 'East Asian' },
      { id: 'south-asian', label: 'South Asian' },
      { id: 'black', label: 'Black' },
      { id: 'white', label: 'White' },
      { id: 'hispanic', label: 'Hispanic' },
      { id: 'middle-eastern', label: 'Middle Eastern' },
      { id: 'mixed', label: 'Mixed' },
    ],
  },
  {
    id: 'setting',
    label: 'Setting',
    type: 'scene',
    values: [
      { id: 'home-kitchen', label: 'Home Kitchen' },
      { id: 'commercial-kitchen', label: 'Commercial Kitchen' },
      { id: 'dining-room', label: 'Dining Room' },
      { id: 'outdoor', label: 'Outdoor' },
      { id: 'cafe', label: 'Café' },
      { id: 'grocery-store', label: 'Grocery Store' },
      { id: 'farmers-market', label: "Farmer's Market" },
    ],
  },
  {
    id: 'time-of-day',
    label: 'Time of Day',
    type: 'scene',
    values: [
      { id: 'morning', label: 'Morning' },
      { id: 'afternoon', label: 'Afternoon' },
      { id: 'evening', label: 'Evening' },
      { id: 'night', label: 'Night' },
    ],
  },
  {
    id: 'mood',
    label: 'Mood / Atmosphere',
    type: 'scene',
    multiSelect: true,
    values: [
      { id: 'warm', label: 'Warm' },
      { id: 'bright', label: 'Bright' },
      { id: 'moody', label: 'Moody' },
      { id: 'rustic', label: 'Rustic' },
      { id: 'modern', label: 'Modern' },
      { id: 'cozy', label: 'Cozy' },
    ],
  },
  {
    id: 'style',
    label: 'Visual Style',
    type: 'both',
    values: [
      { id: 'photorealistic', label: 'Photorealistic' },
      { id: '35mm-film', label: '35mm Film' },
      { id: 'cinematic', label: 'Cinematic' },
      { id: 'illustration', label: 'Illustration' },
    ],
  },
]

export function getCategoriesForType(type: 'character' | 'scene'): TagCategory[] {
  return TAG_CATEGORIES.filter((c) => c.type === type || c.type === 'both')
}

function categoryForTag(tagId: string): string | null {
  for (const cat of TAG_CATEGORIES) {
    if (cat.values.some((v) => v.id === tagId)) return cat.id
  }
  return null
}

/**
 * Merge template tags with user tags. User-selected tags win within
 * the same category (gender, age, etc.). Template fills gaps.
 */
export function smartMergeTags(userTags: string[], templateTags: string[]): string[] {
  const userCategories = new Set<string>()
  for (const t of userTags) {
    const cat = categoryForTag(t)
    if (cat) userCategories.add(cat)
  }

  const merged = [...userTags]
  for (const t of templateTags) {
    const cat = categoryForTag(t)
    if (cat && userCategories.has(cat)) continue
    if (!merged.includes(t)) merged.push(t)
  }

  return merged
}

export function tagLabel(tagId: string): string {
  for (const cat of TAG_CATEGORIES) {
    const val = cat.values.find((v) => v.id === tagId)
    if (val) return val.label
  }
  return tagId
}
